'use client';

import { TransportLandingSection } from '@app/app/esales/transport/_sections/TransportLandingSection';
import { TransportWizardSection } from '@app/app/esales/transport/_sections/TransportWizardSection/TransportWizardSection';
import { Client } from '@app/domain/model/Client';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { ProductDataTerritories } from '@app/domain/model/eshop/transport/ProductDataTerritories';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { useCallback, useState } from 'react';
import { TransportSessionData } from '@app/service/eshop/transport/actions/saveTransportSession';
import { useEffectOnce } from '@app/lib/use-change-effect/useEffectOnce';

interface Props {
    authState: AuthState;
    clients: Client[];
    backgroundImageSrc: string;
    mobileImageSrc: string;
    mtplPurposes: Nullable<ProductDataPurpose>;
    cascoLeasings: ProductDataLeasing[];
    mtplTerritories: Nullable<ProductDataTerritories>;
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    sessionData: Nullable<TransportSessionData>;
}

export function TransportSection({
    authState,
    clients,
    mobileImageSrc,
    backgroundImageSrc,
    mtplPurposes,
    cascoLeasings,
    mtplTerritories,
    swedbankLogoSrc,
    sebLogoSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    sessionData,
}: Props) {
    const [requestResult, setRequestResult] = useState<Nullable<TransportInsuranceRequestResult>>(null);

    useEffectOnce(() => {
        if (!sessionData) {
            return;
        }

        const updatedRequestResult: TransportInsuranceRequestResult = {
            ...sessionData.requestData,
            showVehicleModelStep: false,
            vehicleInformation: {
                ...sessionData.requestData.vehicleInformation,
                modelCASCO:
                    sessionData.purchaseResult.formData.modelCASCO ??
                    sessionData.requestData.vehicleInformation.modelCASCO,
                modificationCodeCasco:
                    sessionData.purchaseResult.formData.modificationCodeCasco ??
                    sessionData.requestData.vehicleInformation.modificationCodeCasco,
                modificationCodeMtpl:
                    sessionData.purchaseResult.formData.modificationCodeMtpl ??
                    sessionData.requestData.vehicleInformation.modificationCodeMtpl,
                deductiblePackageCodeCasco:
                    sessionData.purchaseResult.formData.deductiblePackageCodeCasco ??
                    sessionData.requestData.vehicleInformation.deductiblePackageCodeCasco,
                deductiblePackageCodeMtpl:
                    sessionData.purchaseResult.formData.deductiblePackageCodeMtpl ??
                    sessionData.requestData.vehicleInformation.deductiblePackageCodeMtpl,
                alarmLevelCasco:
                    sessionData.purchaseResult.formData.alarmLevelCasco ??
                    sessionData.requestData.vehicleInformation.alarmLevelCasco,
                alarmLevelMtpl:
                    sessionData.purchaseResult.formData.alarmLevelMtpl ??
                    sessionData.requestData.vehicleInformation.alarmLevelMtpl,
            },
        };

        setRequestResult(updatedRequestResult);
    });

    const handleSuccess = useCallback((e: TransportInsuranceRequestResult) => {
        setRequestResult(e);
    }, []);

    const handleWizardLeave = useCallback(() => {
        setRequestResult(null);
    }, []);

    if (requestResult != null) {
        return (
            <TransportWizardSection
                requestResult={requestResult}
                onLeave={handleWizardLeave}
                authState={authState}
                clients={clients}
                sebLogoSrc={sebLogoSrc}
                mtplPurposes={mtplPurposes}
                cascoLeasings={cascoLeasings}
                mtplTerritories={mtplTerritories}
                swedbankLogoSrc={swedbankLogoSrc}
                stakeholderLogoSrc={stakeholderLogoSrc}
                vigLogoSrc={vigLogoSrc}
                initialPurchaseResult={sessionData?.purchaseResult}
                initialStep={sessionData?.stepToReturn}
            />
        );
    }

    return (
        <TransportLandingSection
            backgroundImageSrc={backgroundImageSrc}
            onSuccess={handleSuccess}
            mobileImageSrc={mobileImageSrc}
            stakeholderLogoSrc={stakeholderLogoSrc}
            vigLogoSrc={vigLogoSrc}
        />
    );
}
